/**
 * The app's single state hook.
 *
 * Writes are optimistic: the UI moves first, the sheet follows, and a failure
 * rolls the change back and surfaces the reason. Every write re-resolves the
 * task's row from its TaskID inside the repository, so an optimistic update
 * never assumes the row it read earlier is still the right one.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SheetsError } from "../lib/sheets";
import * as taskRepo from "./tasksRepository";
import * as projectRepo from "./projectsRepository";
import { planSetup, runSetup, setupNeeded, type SetupPlan } from "./migrate";
import { reconcile, syncOne } from "./calendarSync";
import type { Project, ProjectDraft, Task, TaskDraft } from "../types";

interface Options {
  token: string | null;
  onUnauthorized: () => void;
}

export function useDashboard({ token, onUnauthorized }: Options) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setup, setSetup] = useState<SetupPlan | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [calendarNote, setCalendarNote] = useState<string | null>(null);
  const calendarId = useRef<string | null>(null);

  const report = useCallback(
    (cause: unknown) => {
      if (cause instanceof SheetsError && cause.status === 401) {
        onUnauthorized();
        return;
      }
      setError(cause instanceof Error ? cause.message : "Something went wrong.");
    },
    [onUnauthorized]
  );

  const say = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast((t) => (t === message ? null : t)), 2600);
  }, []);

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const plan = await planSetup(token);
      if (setupNeeded(plan)) {
        setSetup(plan);
        setTasks([]);
        setProjects([]);
        return;
      }
      setSetup(null);
      const [nextProjects, nextTasks] = await Promise.all([
        projectRepo.listProjects(token),
        taskRepo.list(token),
      ]);
      setProjects(nextProjects);
      setTasks(nextTasks);

      // Drift correction. One list call, then only what actually differs.
      // A calendar problem must never stop the board from working, so this
      // reports into its own notice rather than the main error channel.
      try {
        const byId = new Map(nextProjects.map((p) => [p.id, p]));
        const result = await reconcile(nextTasks, byId, token);
        calendarId.current = result.calendarId;
        if (result.changes.size) {
          await taskRepo.setCalendarEventIds(result.changes, token);
          setTasks((current) =>
            current.map((t) =>
              result.changes.has(t.id)
                ? { ...t, calendarEventId: result.changes.get(t.id)! }
                : t
            )
          );
        }
        const touched = result.created + result.updated + result.deleted;
        setCalendarNote(
          result.failures
            ? `${result.failures} calendar ${result.failures === 1 ? "event" : "events"} could not be synced. They will be retried on the next load.`
            : null
        );
        if (touched && !result.failures) {
          say(`Calendar synced: ${result.created} added, ${result.updated} corrected, ${result.deleted} removed.`);
        }
      } catch (cause) {
        calendarId.current = null;
        setCalendarNote(
          cause instanceof Error
            ? `Calendar sync is unavailable: ${cause.message}`
            : "Calendar sync is unavailable."
        );
      }
    } catch (cause) {
      report(cause);
    } finally {
      setLoading(false);
    }
  }, [token, report, say]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const applySetup = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const result = await runSetup(token);
      say(
        `Set up ${result.rowsNeedingId} task ids and ${result.newProjects.length} projects.`
      );
      await refresh();
    } catch (cause) {
      report(cause);
      setLoading(false);
    }
  }, [token, refresh, report, say]);

  /**
   * Push one task's deadline to the calendar after the sheet write succeeded.
   *
   * Never throws and never blocks: a failed calendar write leaves the task
   * saved and is repaired by the next load's reconcile.
   */
  const pushToCalendar = useCallback(
    async (task: Task): Promise<Task> => {
      if (!token || !calendarId.current) return task;
      const name = projects.find((p) => p.id === task.projectId)?.name ?? task.projectName;
      const eventId = await syncOne(task, name, calendarId.current, token);
      if (eventId === undefined) return task;
      try {
        await taskRepo.setCalendarEventIds(new Map([[task.id, eventId]]), token);
      } catch {
        // The id did not reach the sheet; reconcile will sort it out.
      }
      return { ...task, calendarEventId: eventId };
    },
    [token, projects]
  );

  /* ----------------------------------------------------------- task writes */

  const withRollback = useCallback(
    async (next: Task[], work: () => Promise<void>, previous: Task[]) => {
      setTasks(next);
      try {
        await work();
      } catch (cause) {
        setTasks(previous);
        report(cause);
      }
    },
    [report]
  );

  const createTask = useCallback(
    async (draft: TaskDraft) => {
      if (!token) return;
      const previous = tasks;
      const optimistic: Task = {
        ...draft,
        id: `pending-${Math.random().toString(36).slice(2)}`,
        calendarEventId: "",
        deleted: false,
        sheetRow: -1,
      };
      setTasks([...tasks, optimistic]);
      try {
        const saved = await taskRepo.create(draft, token);
        const synced = await pushToCalendar(saved);
        setTasks((current) =>
          current.map((t) => (t.id === optimistic.id ? synced : t))
        );
        say("Task created");
      } catch (cause) {
        setTasks(previous);
        report(cause);
      }
    },
    [token, tasks, report, say, pushToCalendar]
  );

  const updateTask = useCallback(
    async (task: Task) => {
      if (!token) return;
      const previous = tasks;
      await withRollback(
        tasks.map((t) => (t.id === task.id ? task : t)),
        async () => {
          await taskRepo.update(task, token);
          const synced = await pushToCalendar(task);
          if (synced.calendarEventId !== task.calendarEventId) {
            setTasks((current) =>
              current.map((t) => (t.id === synced.id ? synced : t))
            );
          }
          say("Changes saved");
        },
        previous
      );
    },
    [token, tasks, withRollback, say, pushToCalendar]
  );

  const deleteTask = useCallback(
    async (task: Task) => {
      if (!token) return;
      const previous = tasks;
      await withRollback(
        tasks.filter((t) => t.id !== task.id),
        async () => {
          await taskRepo.remove(task, token);
          await pushToCalendar({ ...task, deleted: true });
          say("Task deleted");
        },
        previous
      );
    },
    [token, tasks, withRollback, say, pushToCalendar]
  );

  /* -------------------------------------------------------- project writes */

  const createProject = useCallback(
    async (draft: ProjectDraft): Promise<Project | null> => {
      if (!token) return null;
      try {
        const saved = await projectRepo.createProject(draft, token);
        setProjects((current) => [...current, saved]);
        say("Project created");
        return saved;
      } catch (cause) {
        report(cause);
        return null;
      }
    },
    [token, report, say]
  );

  const updateProject = useCallback(
    async (project: Project, renameTasks = false) => {
      if (!token) return;
      const previousProjects = projects;
      const previousTasks = tasks;
      setProjects(projects.map((p) => (p.id === project.id ? project : p)));
      if (renameTasks) {
        setTasks(
          tasks.map((t) =>
            t.projectId === project.id ? { ...t, projectName: project.name } : t
          )
        );
      }
      try {
        await projectRepo.updateProject(project, token);
        if (renameTasks) {
          // Column B is the human-readable copy of the name. Keep it true.
          const affected = previousTasks.filter((t) => t.projectId === project.id);
          for (const task of affected) {
            await taskRepo.update({ ...task, projectName: project.name }, token);
          }
        }
      } catch (cause) {
        setProjects(previousProjects);
        setTasks(previousTasks);
        report(cause);
      }
    },
    [token, projects, tasks, report]
  );

  const byId = useMemo(
    () => new Map(projects.map((p) => [p.id, p])),
    [projects]
  );

  return {
    tasks,
    projects,
    projectById: byId,
    loading,
    error,
    toast,
    calendarNote,
    setup,
    applySetup,
    refresh,
    createTask,
    updateTask,
    deleteTask,
    createProject,
    updateProject,
    dismissError: () => setError(null),
    dismissCalendarNote: () => setCalendarNote(null),
  };
}
